import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';

import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import {
  AbstractDialog,
  AbstractDialogResultData,
  AbstractFormDialogData,
  FormDialogComponent,
} from '@next/core-lib/dialog';

import { AgrNumberDirective } from '@next/core-lib/form';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { FeedAdvice, FeedAdviceType } from '../../../feed-advice/models/feed-advice.model';
import { getFeedAdvices } from '../../../feed-advice/store/feed-advice.selectors';
import Utils from '../../../utils';

import { FeedAdviceApplication, ProductionType } from '../../models/animal-group.model';
import { setSelectedFeedadviceApplicationId } from '../../store/animal-group.actions';
import { getSelectedFeedadviceApplication } from '../../store/animal-group.selectors';

export interface FeedAdviceApplicationItemDialogInputData extends AbstractFormDialogData {
  productionType: ProductionType;
  id?: number;
}
export interface FeedAdviceApplicationItemDialogResultData {
  feedAdviceApplication: FeedAdviceApplication;
}

@Component({
  selector: 'app-feedadvice-application-item-dialog',
  templateUrl: './feedadvice-application-item-dialog.component.html',
  imports: [
    FormDialogComponent,
    ReactiveFormsModule,
    NgIf,
    MatFormField,
    MatLabel,
    MatSelect,
    NgFor,
    MatOption,
    MatError,
    MatInput,
    AgrNumberDirective,
    MatSuffix,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class FeedadviceApplicationItemDialogComponent
  extends AbstractDialog<FeedAdviceApplicationItemDialogResultData>
  implements OnInit
{
  id: number;

  feedadvices$ = this.store.select(getFeedAdvices).pipe(
    withLatestFrom(this.store.select(UserSelectors.selectLocation)),
    take(1),
    map(([feedadvices, location]: [FeedAdvice[], SelectedLocation]) => {
      feedadvices = feedadvices.filter(
        (feedadvice) => feedadvice.locationIds.findIndex((l) => l === location.id) !== -1,
      );
      switch (this.data.productionType) {
        case ProductionType.FatteningPigs:
          return feedadvices.filter((feedadvice) => feedadvice.type === FeedAdviceType.FATTENING);
        case ProductionType.RearingPigs:
          return feedadvices.filter((feedadvice) => feedadvice.type === FeedAdviceType.REARING);
        case ProductionType.SowsBtF:
          return feedadvices.filter(
            (feedadvice) => feedadvice.type === FeedAdviceType.SOWS_BREEDTOFARROW,
          );
        case ProductionType.SowsFtW:
          return feedadvices.filter(
            (feedadvice) => feedadvice.type === FeedAdviceType.SOWS_FARROWTOWEAN,
          );
        case ProductionType.SowsWtB:
          return feedadvices.filter(
            (feedadvice) => feedadvice.type === FeedAdviceType.SOWS_WEANTOBREED,
          );
        case ProductionType.WeanedPiglets:
          return feedadvices.filter(
            (feedadvice) => feedadvice.type === FeedAdviceType.WEANED_PIGLETS,
          );
      }
    }),
  );

  formGroup = new FormGroup({
    feedadvice: new FormControl<FeedAdvice>(undefined, [Validators.required]),
    applicability: new FormControl<number>(undefined, [
      Validators.required,
      Validators.min(1),
      Validators.max(100),
    ]),
    startDate: new FormControl('', [Validators.required]),
    endDate: new FormControl(''),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FeedAdviceApplicationItemDialogInputData,
    protected mdDialogRef: MatDialogRef<
      FeedadviceApplicationItemDialogComponent,
      AbstractDialogResultData<FeedAdviceApplicationItemDialogResultData>
    >,
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
  ) {
    super();
    this.id = this.data.id;
  }

  ngOnInit(): void {
    if (this.id > 0) {
      this.data.title = this.localeService.translate('animal-group.feedadvices.edit-feedadvice');

      this.store.dispatch(setSelectedFeedadviceApplicationId({ id: this.id }));
      this.store
        .select(getSelectedFeedadviceApplication)
        .pipe(
          withLatestFrom(this.feedadvices$),
          skipWhile(
            ([feedadviceApplication, feedadvices]) =>
              feedadviceApplication === null ||
              feedadviceApplication === undefined ||
              feedadvices?.length === 0,
          ),
          take(1),
        )
        .subscribe(([feedadviceApplication, feedadvices]) => {
          this.formGroup.controls.feedadvice.setValue(
            feedadvices.find((f) => f.id === feedadviceApplication.feedAdviceId),
          );
          this.formGroup.controls.applicability.setValue(
            feedadviceApplication.feedAdviceApplicabilityPercentage,
          );
          this.formGroup.controls.startDate.setValue(feedadviceApplication.startDate);
          this.formGroup.controls.endDate.setValue(feedadviceApplication.endDate);
        });
    } else {
      this.data.title = this.localeService.translate('animal-group.feedadvices.add-feedadvice');
    }
  }

  save() {
    if (this.formGroup.valid) {
      const feedAdviceApplication: FeedAdviceApplication = {
        id: this.id,
        feedAdviceId: (<FeedAdvice>this.formGroup.controls.feedadvice.value).id,
        feedAdviceApplicabilityPercentage: this.formGroup.controls.applicability.value,
        startDate:
          typeof (<unknown>this.formGroup.controls.startDate.value) !== 'string'
            ? Utils.toUtcDate(new Date(this.formGroup.controls.startDate.value)).toISOString()
            : <string>this.formGroup.controls.startDate.value,
        endDate:
          typeof (<unknown>this.formGroup.controls.endDate.value) !== 'string'
            ? Utils.toUtcDate(new Date(this.formGroup.controls.endDate.value)).toISOString()
            : <string>this.formGroup.controls.endDate.value,
      };
      this.confirm({ feedAdviceApplication });
    }
  }
}
